import type { components } from "@connect/data/public/schema";
import { t, type TranslationKey } from "@connect/i18n";
import type {
  AddressErrorField,
  AddressKycFailure,
  ClassifiedKycError,
  DetailsKycFailure,
  KycFailureInput,
  TerminalKycFailure,
} from "./classify-kyc-error.types";

type DevPlatformErrorDetail = components["schemas"]["DevPlatformErrorDetail"];

const TRANSIENT_CODES = new Set([
  "too_many_requests",
  "service_unavailable",
  "sse_timeout",
  "sse_error",
  "unknown_error",
]);

const STATE_CHANGED_CODES = new Set([
  "verification_pending",
  "no_outstanding_requirements",
  "requirement_not_pending",
]);

const TERMINAL_OUTCOMES: Record<string, TerminalKycFailure["outcome"]> = {
  verification_rejected: "rejected",
  kyc_not_approved: "rejected",
  unsupported_country: "unavailable",
  not_allowed: "unavailable",
};

const LOCAL_ERROR_CODES = new Set([
  "missing_dev_platform_access_token",
  "kyc_fetch_failed",
  "kyc_submit_failed",
]);

const ADDRESS_MESSAGES: Record<string, TranslationKey> = {
  "Invalid primary number": "onboarding.errors.address.invalidPrimaryNumber",
  "Sorry, we do not accept PO Box addresses": "onboarding.errors.address.poBox",
  "This address is not supported": "onboarding.errors.address.unsupported",
  "Commercial addresses are not allowed": "onboarding.errors.address.commercial",
  "Missing required secondary info": "onboarding.errors.address.missingSecondary",
  "Your ZIP code is invalid, please try again.": "onboarding.errors.address.invalidZip",
};

const DETAILS_MESSAGES: Record<string, TranslationKey> = {
  "lastName must be at least 2 characters.": "onboarding.errors.details.lastNameTooShort",
  "This field contains unsupported characters.": "onboarding.errors.details.unsupportedCharacters",
  "This field must contain at least one letter.": "onboarding.errors.details.noLetter",
};

const DETAILS_FIELD_FALLBACKS: Record<string, TranslationKey> = {
  "basicDetails.firstName": "onboarding.errors.details.name",
  "basicDetails.lastName": "onboarding.errors.details.name",
  "basicDetails.dateOfBirth": "onboarding.errors.details.dateOfBirth",
};

const ADDRESS_INPUT_FIELDS: Record<string, AddressErrorField> = {
  "residentialAddress.postalCode": "postalCode",
  "residentialAddress.administrativeArea": "administrativeArea",
};

function apiErrorCode(error: unknown): string | undefined {
  if (error === null || typeof error !== "object" || !("code" in error)) {
    return undefined;
  }
  return typeof error.code === "string" ? error.code : undefined;
}

function addressFailure(
  error: object,
  fallbackMessage: TranslationKey,
): Pick<AddressKycFailure, "field" | "message"> | undefined {
  const errors = "errors" in error && Array.isArray(error.errors) ? error.errors : [];
  const detail = (errors as Partial<DevPlatformErrorDetail>[]).find((entry) =>
    entry?.field?.startsWith("residentialAddress"),
  );
  if (!detail?.field || !detail.message) {
    return undefined;
  }
  return {
    field: ADDRESS_INPUT_FIELDS[detail.field] ?? "street",
    message: t(ADDRESS_MESSAGES[detail.message] ?? fallbackMessage),
  };
}

function detailsMessage(field: string, message: string | undefined): string {
  const fallback = Object.entries(DETAILS_FIELD_FALLBACKS).find(([prefix]) =>
    field.startsWith(prefix),
  )?.[1];

  return t(DETAILS_MESSAGES[message ?? ""] ?? fallback ?? "onboarding.errors.details.generic");
}

function detailsFailure(
  error: object,
): Pick<DetailsKycFailure, "field" | "fields" | "message"> | undefined {
  const errors = "errors" in error && Array.isArray(error.errors) ? error.errors : [];

  const fields: Record<string, string> = {};

  for (const entry of errors as Partial<DevPlatformErrorDetail>[]) {
    if (entry?.field && !(entry.field in fields)) {
      fields[entry.field] = detailsMessage(entry.field, entry.message);
    }
  }

  const [first] = Object.entries(fields);

  if (!first) {
    return undefined;
  }

  const [field, message] = first;

  return { field, fields, message };
}

export function classifyKycError({
  error,
  httpStatus,
  source,
}: KycFailureInput): ClassifiedKycError {
  const apiCode = apiErrorCode(error);
  const localCode =
    error instanceof Error && (source === "local" || !(error instanceof TypeError))
      ? error.message
      : undefined;
  const code = apiCode ?? localCode;
  const metadata = {
    ...(code === undefined ? {} : { code }),
    ...(httpStatus === undefined ? {} : { httpStatus }),
  };
  const terminalOutcome = apiCode === undefined ? undefined : TERMINAL_OUTCOMES[apiCode];

  if (terminalOutcome !== undefined) {
    return {
      kind: "terminal",
      outcome: terminalOutcome,
      message:
        terminalOutcome === "rejected"
          ? t("onboarding.errors.rejected")
          : t("onboarding.errors.unavailable"),
      ...metadata,
    };
  }

  if (apiCode === "address_confirmation_required") {
    const confirmation =
      typeof error === "object" && error !== null
        ? addressFailure(error, "onboarding.errors.addressConfirmation")
        : undefined;
    return {
      kind: "address_confirmation",
      field: confirmation?.field ?? "street",
      message: confirmation?.message ?? t("onboarding.errors.addressConfirmation"),
      ...metadata,
    };
  }

  const address =
    apiCode === "invalid_request" && typeof error === "object" && error !== null
      ? addressFailure(error, "onboarding.errors.addressFallback")
      : undefined;
  if (address !== undefined) {
    return { kind: "address", ...address, ...metadata };
  }

  const details =
    apiCode === "invalid_request" && typeof error === "object" && error !== null
      ? detailsFailure(error)
      : undefined;

  if (details !== undefined) {
    return { kind: "details", ...details, ...metadata };
  }

  if (apiCode !== undefined && STATE_CHANGED_CODES.has(apiCode)) {
    return { kind: "state_changed", message: t("onboarding.errors.submission"), ...metadata };
  }

  if (source === "local" || (localCode !== undefined && LOCAL_ERROR_CODES.has(localCode))) {
    return { kind: "submission", message: t("onboarding.errors.submission"), ...metadata };
  }

  if (
    (apiCode !== undefined && TRANSIENT_CODES.has(apiCode)) ||
    httpStatus === 429 ||
    (httpStatus !== undefined && httpStatus >= 500 && httpStatus <= 599)
  ) {
    return { kind: "transient", message: t("onboarding.errors.submission"), ...metadata };
  }

  if (source === "transport" || error instanceof TypeError) {
    return { kind: "transient", message: t("onboarding.errors.loadAccount") };
  }

  return { kind: "submission", message: t("onboarding.errors.submission"), ...metadata };
}
